import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { PageLayout, ActionButton } from '../../components/DesignSystem';
import { message, Modal, DatePicker, Card, Button, Space } from 'antd';
import { AuditOutlined, InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import { PDFDownloadLink } from '@react-pdf/renderer';
import StatementPDF from '../../components/StatementPDF';
import CreditNotePDF from '../../components/CreditNotePDF';
import dayjs from 'dayjs';
import API_URL from '../../config/api';
import { apiFetch } from '../../utils/apiFetch';
import { showToast } from '../../utils/toast';
import { ListPagination, paginateList } from '../../components/ListPagination';
import '../../styles/listPage.css';

type CreditNote = {
  id: number;
  customer_id: number;
  customer_name: string;
  customer_email: string;
  linked_invoice_id?: number;
  linked_invoice_number?: string;
  invoice_number?: string | number; // legacy alias
  amount: number;
  remaining_balance: number;
  reason: string;
  status: 'active' | 'fully_applied' | 'voided';
  issued_at: string;
  created_at: string;
  void_reason?: string;
};

type CreditNoteApplication = {
  id: number;
  invoice_number?: string;
  invoice_id?: number;
  client_name: string;
  amount_applied: number;
  applied_by_name: string;
  applied_at: string;
};

const CreditNotes: React.FC = () => {
  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');
  const [listPage, setListPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [selectedCreditNote, setSelectedCreditNote] = useState<CreditNote | null>(null);
  const [applicationHistory, setApplicationHistory] = useState<CreditNoteApplication[]>([]);
  const [historyByNoteId, setHistoryByNoteId] = useState<Record<number, CreditNoteApplication[]>>({});
  const [showVoidModal, setShowVoidModal] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  
  // Statement Modal State
  const [statementModalVisible, setStatementModalVisible] = useState(false);
  const [statementDateRange, setStatementDateRange] = useState<[any, any]>([
    dayjs().startOf('month'),
    dayjs().endOf('day')
  ]);
  const [statementData, setStatementData] = useState<any>(null);
  const [loadingStatement, setLoadingStatement] = useState(false);
  const [businessSettings, setBusinessSettings] = useState<any>(null);

  const customerId = searchParams.get('customer_id');
  const customerIdNumber = customerId && !Number.isNaN(Number(customerId)) ? Number(customerId) : null;

  // Create form states
  const [customers, setCustomers] = useState<Array<{ id: number; name: string; credit_limit?: number }>>([]);
  const [agingReport, setAgingReport] = useState<Array<{ customer_id: number; customer_name: string; total_owed: number }>>([]);
  const [activeTab, setActiveTab] = useState<'notes' | 'accounts'>('notes');
  useEffect(() => {
    // Load business settings for PDF header
    const loadSettings = async () => {
      try {
        const res = await apiFetch(`${API_URL}/business`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setBusinessSettings(data);
        }
      } catch (e) {
        console.error('Failed to load business settings:', e);
      }
    };
    if (token) loadSettings();
  }, [token]);

  const fetchStatementData = async (cId?: number) => {
    const targetId = cId || customerId;
    if (!targetId) return;
    
    setLoadingStatement(true);
    try {
      const start = statementDateRange[0].format('YYYY-MM-DD');
      const end = statementDateRange[1].format('YYYY-MM-DD');
      const res = await apiFetch(`${API_URL}/customers/${targetId}/statement?start=${start}&end=${end}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setStatementData(data);
      } else {
        message.error('Failed to fetch statement data');
      }
    } catch (e) {
      console.error(e);
      message.error('Error loading statement');
    } finally {
      setLoadingStatement(false);
    }
  };
  const [createForm, setCreateForm] = useState({
    customer_id: '',
    amount: '',
    reason: 'refund' as 'refund' | 'goodwill' | 'discount' | 'overpayment'
  });

  const statementCustomerId = selectedCreditNote?.customer_id || customerIdNumber;

  useEffect(() => {
    fetchCreditNotes();
    fetchCustomers();
    fetchAgingReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, navigate, customerId]);

  useEffect(() => {
    setListPage(1);
  }, [searchTerm, statusFilter, customerId]);

  const fetchCustomers = async () => {
    try {
      if (!token) return;
      const res = await apiFetch(`${API_URL}/customers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data);
      }
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const fetchAgingReport = async () => {
    try {
      if (!token) return;
      const res = await apiFetch(`${API_URL}/invoices/reports/aging`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setAgingReport(Array.isArray(data.report) ? data.report : []);
      }
    } catch (error) {
      console.error('Error fetching aging report:', error);
    }
  };

  const fetchCreditNotes = async () => {
    try {
      setLoading(true);
      if (!token) {
        navigate('/login');
        return;
      }

      const url = customerId
        ? `${API_URL}/credit-notes?customer_id=${customerId}`
        : `${API_URL}/credit-notes`;

      const res = await apiFetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }

      const data = await res.json();
      const normalized = (Array.isArray(data) ? data : []).map((cn: any) => ({
        ...cn,
        linked_invoice_id: cn.linked_invoice_id ?? cn.invoice_id ?? null,
        linked_invoice_number: cn.linked_invoice_number ?? cn.invoice_number ?? null,
      }));
      setCreditNotes(normalized);
    } catch (error) {
      console.error('Error fetching credit notes:', error);
      message.error('Failed to load credit notes');
      setCreditNotes([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCredit = async () => {
    if (!createForm.customer_id || !createForm.amount || parseFloat(createForm.amount) <= 0) {
      showToast('Please fill in all required fields');
      return;
    }

    try {
      const res = await apiFetch(`${API_URL}/credit-notes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: parseInt(createForm.customer_id),
          amount: parseFloat(createForm.amount),
          reason: createForm.reason
        })
      });

      if (res.ok) {
        message.success('✅ Credit note created successfully');
        setShowCreateModal(false);
        setCreateForm({ customer_id: '', amount: '', reason: 'refund' });
        fetchCreditNotes();
      } else {
        const error = await res.json();
        message.error(`Failed to create credit note: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error creating credit note:', error);
      message.error('Network error while creating credit note');
    }
  };

  const handleViewHistory = async (creditNote: CreditNote) => {
    setSelectedCreditNote(creditNote);
    setShowHistoryModal(true);

    try {
      const res = await apiFetch(`${API_URL}/credit-notes/${creditNote.id}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setApplicationHistory(data);
        setHistoryByNoteId(prev => ({ ...prev, [creditNote.id]: data }));
      }
    } catch (error) {
      console.error('Error fetching credit history:', error);
    }
  };

  const loadHistoryForPdf = async (creditNote: CreditNote) => {
    if (historyByNoteId[creditNote.id]) return;
    try {
      const res = await apiFetch(`${API_URL}/credit-notes/${creditNote.id}/history`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setHistoryByNoteId(prev => ({ ...prev, [creditNote.id]: data }));
      }
    } catch { /* PDF still works without history */ }
  };

  const handleVoidCredit = async () => {
    if (!selectedCreditNote || !voidReason.trim()) {
      showToast('Please provide a void reason');
      return;
    }

    try {
      const res = await apiFetch(`${API_URL}/credit-notes/${selectedCreditNote.id}/void`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ void_reason: voidReason })
      });

      if (res.ok) {
        message.success('✅ Credit note voided successfully');
        setShowVoidModal(false);
        setVoidReason('');
        setSelectedCreditNote(null);
        fetchCreditNotes();
      } else {
        const error = await res.json();
        message.error(`Failed to void credit note: ${error.error || 'Unknown error'}`);
      }
    } catch (error) {
      console.error('Error voiding credit note:', error);
      message.error('Network error while voiding credit note');
    }
  };

  const filteredCreditNotes = creditNotes.filter(cn => {
    const matchesStatus = statusFilter === 'All' || cn.status === statusFilter;
    const q = searchTerm.toLowerCase();
    const matchesSearch = !searchTerm ||
      cn.customer_name?.toLowerCase().includes(q) ||
      cn.reason?.toLowerCase().includes(q) ||
      cn.customer_email?.toLowerCase().includes(q);
    return matchesStatus && matchesSearch;
  });

  const { pages: creditPages, safePage: safeListPage, slice: paginatedCreditNotes } =
    paginateList(filteredCreditNotes, listPage);

  const renderCreditNoteActions = (cn: CreditNote) => (
    <div style={styles.actionButtons}>
      <ActionButton variant="view" label="History" onClick={() => handleViewHistory(cn)} />
      <PDFDownloadLink
        document={<CreditNotePDF creditNote={cn} businessSettings={businessSettings} applicationHistory={historyByNoteId[cn.id] || []} />}
        fileName={`credit-note-${cn.id}.pdf`}
        style={{ textDecoration: 'none' }}
        onClick={() => loadHistoryForPdf(cn)}
      >
        {({ loading: pdfLoading }) => (
          <button
            title="Download PDF"
            style={{ padding: '4px 10px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, cursor: pdfLoading ? 'wait' : 'pointer', fontSize: 12, fontWeight: 600 }}
          >
            {pdfLoading ? '...' : '📄 PDF'}
          </button>
        )}
      </PDFDownloadLink>
      {cn.status === 'active' && (
        <ActionButton variant="delete" label="Void" onClick={() => { setSelectedCreditNote(cn); setShowVoidModal(true); }} />
      )}
    </div>
  );

  const exportCreditNotesCSV = () => {
    if (!filteredCreditNotes.length) {
      message.warning('No credit notes to export');
      return;
    }

    const escapeCsv = (value: any) => {
      const text = value == null ? '' : String(value);
      if (/[,"\n]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const headers = ['ID', 'Customer', 'Email', 'Invoice', 'Amount', 'Remaining', 'Reason', 'Status', 'Issued Date', 'Created At'];
    const rows = filteredCreditNotes.map(cn => [
      cn.id,
      cn.customer_name,
      cn.customer_email,
      cn.linked_invoice_number ?? cn.invoice_number ?? '',
      `£${Number(cn.amount || 0).toFixed(2)}`,
      `£${Number(cn.remaining_balance || 0).toFixed(2)}`,
      cn.reason,
      getStatusLabel(cn.status),
      new Date(cn.issued_at).toLocaleDateString('en-GB'),
      new Date(cn.created_at).toLocaleDateString('en-GB'),
    ].map(escapeCsv).join(','));

    const csv = `data:text/csv;charset=utf-8,${headers.map(escapeCsv).join(',')}\n${rows.join('\n')}`;
    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csv));
    link.setAttribute('download', `credit_notes_${new Date().toISOString().slice(0,10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const totalActive = creditNotes
    .filter(cn => cn.status === 'active')
    .reduce((sum, cn) => sum + cn.remaining_balance, 0);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return '#10b981';
      case 'fully_applied': return '#6b7280';
      case 'voided': return '#ef4444';
      default: return '#9ca3af';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Active';
      case 'fully_applied': return 'Fully Applied';
      case 'voided': return 'Voided';
      default: return status;
    }
  };

  const getReasonColor = (reason: string) => {
    switch (reason) {
      case 'refund': return '#3b82f6';
      case 'goodwill': return '#8b5cf6';
      case 'discount': return '#f59e0b';
      case 'overpayment': return '#10b981';
      default: return '#6b7280';
    }
  };

  return (
    <PageLayout
      title="💰 Credit Notes"
      action={
        <div style={{ display: 'flex', gap: '10px' }}>
          {customerId && (
            <button
              onClick={() => {
                const cn = creditNotes.find(c => String(c.customer_id) === String(customerId));
                setSelectedCreditNote(cn || null);
                setStatementModalVisible(true);
              }}
              style={{
                padding: '10px 20px',
                background: '#fa8c16',
                color: 'white',
                border: 'none',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600
              }}
            >
              📄 Customer Statement
            </button>
          )}
          <Button
            icon={<DownloadOutlined />}
            onClick={exportCreditNotesCSV}
            style={{
              padding: '10px 20px',
              background: '#2563eb',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 8
            }}
          >
            Export CSV
          </Button>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              padding: '10px 20px',
              background: '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 600
            }}
          >
            ➕ Create Credit Note
          </button>
        </div>
      }
    >
      {/* Tab Toggle */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f3f4f6', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        <button
          onClick={() => setActiveTab('notes')}
          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: activeTab === 'notes' ? '#fff' : 'transparent', color: activeTab === 'notes' ? '#111827' : '#6b7280', boxShadow: activeTab === 'notes' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
        >
          💳 Credit Notes
        </button>
        <button
          onClick={() => setActiveTab('accounts')}
          style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14, background: activeTab === 'accounts' ? '#fff' : 'transparent', color: activeTab === 'accounts' ? '#111827' : '#6b7280', boxShadow: activeTab === 'accounts' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none' }}
        >
          🏦 Credit Accounts
        </button>
      </div>

      {activeTab === 'accounts' ? (
        /* ── CREDIT ACCOUNTS VIEW ─────────────────────────────── */
        <div>
          <div style={{ marginBottom: 16, color: '#6b7280', fontSize: 14 }}>
            Set a customer's credit limit on their profile (Edit Customer → Credit Limit). This table shows who is over their limit.
          </div>
          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
                <tr>
                  <th style={styles.th}>Customer</th>
                  <th style={styles.th}>Credit Limit</th>
                  <th style={styles.th}>Outstanding Invoices</th>
                  <th style={styles.th}>Available</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Action</th>
                </tr>
              </thead>
              <tbody>
                {customers
                  .filter((c: any) => (c.credit_limit || 0) > 0 || agingReport.some(a => a.customer_id === c.id && parseFloat(String(a.total_owed)) > 0))
                  .map((c: any) => {
                    const aging = agingReport.find(a => a.customer_id === c.id);
                    const outstanding = parseFloat(String(aging?.total_owed || 0));
                    const limit = parseFloat(String(c.credit_limit || 0));
                    const available = limit > 0 ? limit - outstanding : null;
                    const isOver = limit > 0 && outstanding > limit;
                    const isNear = limit > 0 && !isOver && outstanding > limit * 0.8;
                    return (
                      <tr key={c.id} style={{ ...styles.tr, background: isOver ? '#fef2f2' : 'white' }}>
                        <td style={styles.td}>
                          <div style={{ fontWeight: 600, color: '#111827' }}>{c.name}</div>
                          {c.companyname && <div style={{ fontSize: 12, color: '#6b7280' }}>{c.companyname}</div>}
                        </td>
                        <td style={styles.td}>{limit > 0 ? `£${limit.toFixed(2)}` : <span style={{ color: '#9ca3af' }}>No limit set</span>}</td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 600, color: outstanding > 0 ? '#dc2626' : '#10b981' }}>
                            £{outstanding.toFixed(2)}
                          </span>
                        </td>
                        <td style={styles.td}>
                          {available !== null ? (
                            <span style={{ fontWeight: 600, color: available < 0 ? '#dc2626' : '#10b981' }}>
                              {available < 0 ? `-£${Math.abs(available).toFixed(2)}` : `£${available.toFixed(2)}`}
                            </span>
                          ) : <span style={{ color: '#9ca3af' }}>—</span>}
                        </td>
                        <td style={styles.td}>
                          <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600, color: 'white', background: isOver ? '#dc2626' : isNear ? '#f59e0b' : '#10b981' }}>
                            {isOver ? '🚨 Over Limit' : isNear ? '⚠️ Near Limit' : '✅ OK'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <button
                            onClick={() => navigate(`/customers/edit/${c.id}`)}
                            style={{ padding: '4px 12px', background: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 13 }}
                          >
                            Edit Limit
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                {customers.filter((c: any) => (c.credit_limit || 0) > 0 || agingReport.some(a => a.customer_id === c.id && parseFloat(String(a.total_owed)) > 0)).length === 0 && (
                  <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: '#9ca3af' }}>No customers with credit limits or outstanding balances.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
      <>
      {/* Filter Bar */}
      <div style={styles.filterBar}>
        <input
          placeholder="Search by customer or reason..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid #d1d5db', fontSize: 14, minWidth: 260, outline: 'none' }}
        />
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Status:</label>
          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={styles.select}
          >
            <option value="All">All</option>
            <option value="active">Active</option>
            <option value="fully_applied">Fully Applied</option>
            <option value="voided">Voided</option>
          </select>
        </div>
        {(searchTerm || customerId) && (
          <button
            onClick={() => { setSearchTerm(''); if (customerId) navigate('/credit-notes'); }}
            style={styles.clearFilterButton}
          >
            Clear Filters
          </button>
        )}
      </div>

      {/* Summary Stats */}
      <div style={styles.stats}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Active Credits</div>
          <div style={{...styles.statValue, color: '#10b981'}}>
            £{Number(totalActive || 0).toFixed(2)}
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Active Credit Notes</div>
          <div style={styles.statValue}>
            {creditNotes.filter(cn => cn.status === 'active').length}
          </div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Total Credit Notes</div>
          <div style={styles.statValue}>
            {creditNotes.length}
          </div>
        </div>
      </div>

      {/* Credit Notes Table */}
      {loading ? (
        <div style={styles.loading}>Loading credit notes...</div>
      ) : filteredCreditNotes.length === 0 ? (
        <div style={styles.empty}>No credit notes found</div>
      ) : (
        <>
        <ListPagination
          total={creditNotes.length}
          filtered={filteredCreditNotes.length}
          page={safeListPage}
          pages={creditPages}
          onPageChange={setListPage}
          noun="credit notes"
        />
        <div className="list-table-panel">
        <div style={styles.tableContainer}>
          <table style={styles.table}>
            <thead style={{ backgroundColor: '#fafafa', borderBottom: '1px solid #f0f0f0' }}>
              <tr>
                <th style={styles.th}>ID</th>
                <th style={styles.th}>Customer</th>
                <th style={styles.th}>Invoice</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Remaining</th>
                <th style={styles.th}>Reason</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Issued Date</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedCreditNotes.map(cn => (
                <tr key={cn.id} style={styles.tr}>
                  <td style={styles.td}><strong>#{cn.id}</strong></td>
                  <td style={styles.td}>
                    <div
                      style={{ fontWeight: 600, color: '#2563eb', cursor: 'pointer' }}
                      onClick={() => navigate(`/customers/view/${cn.customer_id}`)}
                    >
                      {cn.customer_name}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>{cn.customer_email}</div>
                  </td>
                  <td style={styles.td}>
                    {(cn.linked_invoice_id || cn.linked_invoice_number) ? (
                      <span
                        style={{ color: '#2563eb', cursor: 'pointer', fontWeight: 500 }}
                        onClick={() => navigate(`/invoices/view/${cn.linked_invoice_id}`)}
                      >
                        #{cn.linked_invoice_number || cn.linked_invoice_id}
                      </span>
                    ) : (
                      <span style={{ color: '#9ca3af', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td style={styles.td}>£{Number(cn.amount || 0).toFixed(2)}</td>
                  <td style={styles.td}>
                    <span style={{ fontWeight: 600, color: Number(cn.remaining_balance || 0) > 0 ? '#10b981' : '#6b7280' }}>
                      £{Number(cn.remaining_balance || 0).toFixed(2)}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, backgroundColor: getReasonColor(cn.reason) }}>
                      {cn.reason ? cn.reason.charAt(0).toUpperCase() + cn.reason.slice(1) : '—'}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <span style={{ ...styles.statusBadge, backgroundColor: getStatusColor(cn.status) }}>
                      {getStatusLabel(cn.status)}
                    </span>
                  </td>
                  <td style={styles.td}>
                    {new Date(cn.issued_at).toLocaleDateString('en-GB')}
                  </td>
                  <td style={styles.td}>
                    {renderCreditNoteActions(cn)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </div>

        <div className="list-mobile-cards">
          {paginatedCreditNotes.map(cn => (
            <div key={cn.id} className="list-mobile-card">
              <div className="list-mobile-card-header">
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div className="list-mobile-card-title">Credit #{cn.id}</div>
                  <div className="list-mobile-card-meta">{cn.customer_name}</div>
                </div>
                <span style={{ ...styles.statusBadge, backgroundColor: getStatusColor(cn.status) }}>
                  {getStatusLabel(cn.status)}
                </span>
              </div>
              <div className="list-mobile-card-info">
                <div><div className="list-mobile-card-label">Amount</div>£{Number(cn.amount || 0).toFixed(2)}</div>
                <div><div className="list-mobile-card-label">Remaining</div>£{Number(cn.remaining_balance || 0).toFixed(2)}</div>
                <div><div className="list-mobile-card-label">Reason</div>{cn.reason || '—'}</div>
                <div><div className="list-mobile-card-label">Issued</div>{new Date(cn.issued_at).toLocaleDateString('en-GB')}</div>
              </div>
              <div className="list-mobile-actions">{renderCreditNoteActions(cn)}</div>
            </div>
          ))}
        </div>
        </>
      )}

      {/* Create Credit Modal */}
      {showCreateModal && (
        <div style={styles.modalOverlay} onClick={() => setShowCreateModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Create Credit Note</h2>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Customer *</label>
                <select
                  value={createForm.customer_id}
                  onChange={e => setCreateForm({...createForm, customer_id: e.target.value})}
                  style={styles.input}
                >
                  <option value="">-- Select Customer --</option>
                  {customers.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                {(() => {
                  if (!createForm.customer_id) return null;
                  const cust = customers.find((c: any) => String(c.id) === createForm.customer_id) as any;
                  const aging = agingReport.find(a => a.customer_id === Number(createForm.customer_id));
                  const outstanding = parseFloat(String(aging?.total_owed || 0));
                  const limit = parseFloat(String(cust?.credit_limit || 0));
                  if (limit <= 0 && outstanding <= 0) return null;
                  const isOver = limit > 0 && outstanding > limit;
                  return (
                    <div style={{ marginTop: 8, padding: '8px 12px', borderRadius: 8, background: isOver ? '#fef2f2' : '#f0fdf4', border: `1px solid ${isOver ? '#fca5a5' : '#bbf7d0'}`, fontSize: 13 }}>
                      <strong>Outstanding:</strong> £{outstanding.toFixed(2)}
                      {limit > 0 && <> &nbsp;·&nbsp; <strong>Limit:</strong> £{limit.toFixed(2)}</>}
                      {isOver && <span style={{ color: '#dc2626', fontWeight: 700 }}> — ⚠️ Over credit limit</span>}
                    </div>
                  );
                })()}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Amount (£) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={createForm.amount}
                  onChange={e => setCreateForm({...createForm, amount: e.target.value})}
                  style={styles.input}
                  placeholder="0.00"
                />
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Reason *</label>
                <select
                  value={createForm.reason}
                  onChange={e => setCreateForm({...createForm, reason: e.target.value as any})}
                  style={styles.input}
                >
                  <option value="refund">Refund</option>
                  <option value="goodwill">Goodwill</option>
                  <option value="discount">Discount</option>
                  <option value="overpayment">Overpayment (Manual)</option>
                </select>
              </div>
            </div>

            <div style={styles.modalActions}>
              <button style={styles.submitButton} onClick={handleCreateCredit}>
                Create Credit Note
              </button>
              <button style={styles.cancelButton} onClick={() => setShowCreateModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Statement Generation Modal */}
      <Modal
        title={<span><AuditOutlined /> Statement of Account</span>}
        open={statementModalVisible}
        onCancel={() => {
          setStatementModalVisible(false);
          setStatementData(null);
        }}
        footer={null}
        width={600}
      >
        <div style={{ padding: '10px 0' }}>
          <p>Generate a branded Statement of Account for <strong>{statementData?.customer?.name || selectedCreditNote?.customer_name || 'this customer'}</strong>.</p>
          
          <div style={{ marginBottom: '20px', marginTop: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>Select Statement Period:</label>
            <DatePicker.RangePicker 
              style={{ width: '100%' }}
              value={statementDateRange}
              onChange={(dates: any) => dates && setStatementDateRange([dates[0], dates[1]])}
              presets={[
                { label: 'This Month', value: [dayjs().startOf('month'), dayjs().endOf('month')] },
                { label: 'Last Month', value: [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')] },
                { label: 'Last 90 Days', value: [dayjs().subtract(90, 'days'), dayjs()] },
                { label: 'Year to Date', value: [dayjs().startOf('year'), dayjs()] },
              ]}
            />
          </div>

          <Card style={{ backgroundColor: '#f9fafb', marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontSize: '12px', color: '#666' }}>Transaction Preview</div>
                <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                  {statementData ? `${statementData.transactions.length} items found` : 'Click Fetch to preview'}
                </div>
              </div>
              <Button 
                type="primary" 
                onClick={() => fetchStatementData(statementCustomerId || undefined)} 
                loading={loadingStatement}
                disabled={!statementCustomerId}
              >
                Fetch Statement Data
              </Button>
            </div>
          </Card>

          {statementData && (
            <div style={{ borderTop: '1px solid #eee', paddingTop: '20px', textAlign: 'center' }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <PDFDownloadLink
                  document={<StatementPDF data={statementData} businessSettings={businessSettings} />}
                  fileName={`Statement_${statementData.customer.name.replace(/\s+/g, '_')}_${statementDateRange[0].format('YYYY-MM-DD')}.pdf`}
                  style={{
                    textDecoration: 'none',
                    padding: '12px 24px',
                    color: '#fff',
                    backgroundColor: '#10b981',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    display: 'inline-block',
                    width: '100%',
                    textAlign: 'center'
                  }}
                >
                  {({ loading }) => (loading ? <span>📄 Preparing PDF...</span> : <span>📥 Download Statement PDF</span>)}
                </PDFDownloadLink>
                
                <Button 
                  icon={<InboxOutlined />} 
                  style={{ width: '100%', height: '45px', marginTop: '10px' }}
                  onClick={() => {
                    message.info('Email statement feature coming in Phase 5!');
                  }}
                >
                  Send to Customer via Email
                </Button>
              </Space>
            </div>
          )}
        </div>
      </Modal>

      {/* History Modal */}
      {showHistoryModal && selectedCreditNote && (
        <div style={styles.modalOverlay} onClick={() => setShowHistoryModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Credit Note #{selectedCreditNote.id} - Application History</h2>
            <div style={styles.modalBody}>
              <p><strong>Customer:</strong> {selectedCreditNote.customer_name}</p>
              <p><strong>Original Amount:</strong> £{Number(selectedCreditNote.amount || 0).toFixed(2)}</p>
              <p><strong>Remaining:</strong> £{Number(selectedCreditNote.remaining_balance || 0).toFixed(2)}</p>

              {applicationHistory.length === 0 ? (
                <div style={styles.empty}>No applications yet</div>
              ) : (
                <div style={styles.historyList}>
                  <h3 style={{ marginTop: 20, marginBottom: 10 }}>Applications:</h3>
                  {applicationHistory.map((app, idx) => (
                    <div key={idx} style={styles.historyItem}>
                      <div>
                        <strong>Invoice #{app.invoice_number || app.invoice_id}</strong> - {app.client_name}
                      </div>
                      <div style={{ fontSize: 14, color: '#666' }}>
                        £{Number(app.amount_applied || 0).toFixed(2)} applied by {app.applied_by_name} on{' '}
                        {new Date(app.applied_at).toLocaleDateString('en-GB')}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={styles.modalActions}>
              <button style={styles.cancelButton} onClick={() => setShowHistoryModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Void Modal */}
      {showVoidModal && selectedCreditNote && (
        <div style={styles.modalOverlay} onClick={() => setShowVoidModal(false)}>
          <div style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h2 style={styles.modalTitle}>Void Credit Note #{selectedCreditNote.id}</h2>
            <div style={styles.modalBody}>
              <p><strong>Customer:</strong> {selectedCreditNote.customer_name}</p>
              <p><strong>Amount:</strong> £{Number(selectedCreditNote.amount || 0).toFixed(2)}</p>
              <p><strong>Remaining:</strong> £{Number(selectedCreditNote.remaining_balance || 0).toFixed(2)}</p>

              <div style={{
                background: '#fee2e2',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '16px',
                color: '#991b1b'
              }}>
                ⚠️ Warning: Voiding this credit note cannot be undone. The credit will no longer be available for use.
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Void Reason *</label>
                <textarea
                  value={voidReason}
                  onChange={e => setVoidReason(e.target.value)}
                  style={styles.textarea}
                  placeholder="Enter reason for voiding..."
                  rows={3}
                />
              </div>
            </div>

            <div style={styles.modalActions}>
              <button
                style={{...styles.submitButton, background: '#ef4444'}}
                onClick={handleVoidCredit}
              >
                Void Credit Note
              </button>
              <button style={styles.cancelButton} onClick={() => setShowVoidModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </>
    )}
    </PageLayout>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  filterBar: {
    display: 'flex',
    gap: 12,
    marginBottom: 20,
    flexWrap: 'wrap',
    alignItems: 'center',
    background: '#fff',
    padding: 20,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  filterGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8
  },
  filterLabel: {
    fontSize: 14,
    fontWeight: 600,
    color: '#374151'
  },
  select: {
    padding: '8px 12px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    fontSize: 14,
    background: 'white'
  },
  clearFilterButton: {
    padding: '8px 16px',
    background: '#ef4444',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 500
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: 20,
    marginBottom: 24
  },
  statCard: {
    background: 'white',
    padding: 20,
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)'
  },
  statLabel: {
    fontSize: 14,
    color: '#6b7280',
    marginBottom: 8
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    color: '#111827'
  },
  loading: {
    textAlign: 'center',
    padding: 40,
    fontSize: 16,
    color: '#666'
  },
  empty: {
    textAlign: 'center',
    padding: 40,
    fontSize: 16,
    color: '#999'
  },
  tableContainer: {
    background: 'white',
    borderRadius: 12,
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    overflow: 'auto'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    minWidth: 900
  },
  th: {
    padding: 16,
    textAlign: 'left',
    fontWeight: 500,
    fontSize: 14,
    color: 'rgba(0,0,0,0.85)',
    whiteSpace: 'nowrap'
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
    transition: 'background 0.2s'
  },
  td: {
    padding: 16,
    fontSize: 14,
    color: 'rgba(0,0,0,0.85)'
  },
  statusBadge: {
    padding: '4px 12px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 600,
    color: 'white',
    display: 'inline-block'
  },
  actionButtons: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap'
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  },
  modalContent: {
    background: 'white',
    borderRadius: 12,
    maxWidth: 600,
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
    padding: '20px 24px',
    borderBottom: '1px solid #e5e7eb'
  },
  modalBody: {
    padding: 24
  },
  formGroup: {
    marginBottom: 16
  },
  label: {
    display: 'block',
    fontSize: 14,
    fontWeight: 600,
    marginBottom: 6,
    color: '#374151'
  },
  input: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    fontSize: 14,
    boxSizing: 'border-box'
  },
  textarea: {
    width: '100%',
    padding: '10px 12px',
    borderRadius: 8,
    border: '1px solid #d1d5db',
    fontSize: 14,
    boxSizing: 'border-box',
    fontFamily: 'inherit',
    resize: 'vertical'
  },
  modalActions: {
    padding: '16px 24px',
    borderTop: '1px solid #e5e7eb',
    display: 'flex',
    gap: 12,
    justifyContent: 'flex-end'
  },
  submitButton: {
    padding: '10px 20px',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600
  },
  cancelButton: {
    padding: '10px 20px',
    background: '#6b7280',
    color: 'white',
    border: 'none',
    borderRadius: 8,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: 600
  },
  historyList: {
    marginTop: 16
  },
  historyItem: {
    padding: 12,
    background: '#f9fafb',
    borderRadius: 8,
    marginBottom: 8
  }
};

export default CreditNotes;
